import * as z from 'zod'

import type { TDoc, TEntity } from '#firebase/firestoreSchemas'

import { birthdaySchema, zipCodeSchema } from '../schemas'

export enum Ownership {
  Person = 'person',
  Partner = 'partner',
  Joint = 'joint',
}

export const personId = 'person'
export const partnerId = 'partner'

export const RETIREMENT_AGE_MIN = 18
export const RETIREMENT_AGE_MAX = 100

export const memberSchema = z.object({
  // Optional since the onboarding wizard (ZL-1314) saves a profile before anyone is asked.
  name: z.string().trim().default(''),
  birthday: birthdaySchema,
  retirementAge: z
    .int('Retirement age is required')
    .min(
      RETIREMENT_AGE_MIN,
      `Enter an age between ${RETIREMENT_AGE_MIN} and ${RETIREMENT_AGE_MAX}`
    )
    .max(
      RETIREMENT_AGE_MAX,
      `Enter an age between ${RETIREMENT_AGE_MIN} and ${RETIREMENT_AGE_MAX}`
    ),
  socialSecurityAge: z
    .int('Social Security age must be a whole number')
    .min(62, 'Enter an age between 62 and 70')
    .max(70, 'Enter an age between 62 and 70')
    .nullable()
    .default(null),
  zipCode: zipCodeSchema,
})
export type Member = z.infer<typeof memberSchema>

export const maritalStatusSchema = z.enum(
  ['single', 'married'],
  'Please select a marital status'
)
export type MaritalStatus = z.infer<typeof maritalStatusSchema>

export const profileSchema = z
  .object({
    maritalStatus: maritalStatusSchema,
    members: z.object({
      [personId]: memberSchema,
      [partnerId]: memberSchema.nullable(),
    }),
    jointFinances: z.boolean().default(true),
    filingJointly: z.boolean().default(true),
    minorChildrenCount: z
      .int()
      .min(0, 'Enter a number between 0 and 10')
      .max(10, 'Enter a number between 0 and 10')
      .default(0),
    otherDependentsCount: z
      .int()
      .min(0, 'Enter a number between 0 and 10')
      .max(10, 'Enter a number between 0 and 10')
      .default(0),
  })
  .superRefine((data, ctx) => {
    const { maritalStatus, members } = data

    if (maritalStatus === 'married' && !members.partner) {
      ctx.addIssue({
        code: 'custom',
        path: ['members', partnerId],
        message: "Add your partner's details to continue",
      })
    }

    if (maritalStatus === 'single' && members.partner) {
      ctx.addIssue({
        code: 'custom',
        path: ['members', partnerId],
        message: 'Profile data is invalid',
      })
    }
  })

export type Profile = TEntity<typeof profileSchema>
export type ProfileDoc = TDoc<typeof profileSchema>

export const defaultProfile = {
  maritalStatus: 'single',
  members: {
    person: {},
    partner: null,
  },
  jointFinances: true,
  filingJointly: true,
  minorChildrenCount: 0,
  otherDependentsCount: 0,
} as Profile
